import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ZephyrLogo from "@/Images/Logos/Zephyr_Logo.png";
import { isZephyrOnlyUser, resetAdminAuthorityCooldown } from "@/services/adminAuthority";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";

const ZEPHYR_NAV = [
  { label: "Overview",    path: "/zephyr-admin/overview",    Icon: DashboardOutlinedIcon },
  { label: "Invoices",    path: "/zephyr-admin/invoices",    Icon: ReceiptLongOutlinedIcon },
  { label: "Overdue",     path: "/zephyr-admin/overdue",     Icon: ErrorOutlineOutlinedIcon },
  { label: "Customers",   path: "/zephyr-admin/customers",   Icon: PeopleOutlinedIcon },
  { label: "Chemists",    path: "/zephyr-admin/chemists",    Icon: LocalPharmacyOutlinedIcon },
  { label: "Payments",    path: "/zephyr-admin/payments",    Icon: PaymentsOutlinedIcon },
  { label: "Processing",  path: "/zephyr-admin/processing",  Icon: PlayCircleOutlineIcon },
  { label: "Bank Recon",  path: "/zephyr-admin/bank-recon",  Icon: AccountBalanceOutlinedIcon },
  { label: "Statements",  path: "/zephyr-admin/statements",  Icon: DescriptionOutlinedIcon },
  { label: "Tools",       path: "/zephyr-admin/tools",       Icon: BuildOutlinedIcon },
];

export default function ZephyrAdminLayout({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const zephyrOnly = isZephyrOnlyUser();
  const [userInfo, setUserInfo] = React.useState(null);
  const [imageError, setImageError] = React.useState(false);

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem("googleUserInfo");
      if (stored) setUserInfo(JSON.parse(stored));
    } catch {}
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("googleUserInfo");
      localStorage.removeItem("userData");
      localStorage.removeItem("tempGoogleUserInfo");
      localStorage.removeItem("token");
      Object.keys(localStorage).forEach((k) => {
        if (/(token|auth|user|google)/i.test(k)) localStorage.removeItem(k);
      });
      sessionStorage.clear();
      resetAdminAuthorityCooldown();
    } finally {
      setIsAuthenticated?.(false);
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">

      {/* ── Return to PH strip — PH admins only ─────────────────── */}
      {!zephyrOnly && (
        <div style={{
          width: 48,
          backgroundColor: "#002169",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          zIndex: 30,
          boxShadow: "2px 0 8px rgba(0,0,0,0.25)",
          paddingTop: 8,
        }}>
          <Tooltip title="Return to Pharmacy Halo" placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => navigate("/admin-functions/overview")}
              sx={{ color: "rgba(255,255,255,0.75)", "&:hover": { color: "#FFAF3C" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}

      {/* ── Zephyr sub-nav ───────────────────────────────────────── */}
      <div style={{
        width: 200,
        background: `linear-gradient(180deg, ${Z_PURPLE} 0%, #1e0a36 100%)`,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        boxShadow: "2px 0 6px rgba(0,0,0,0.2)",
      }}>
        <div style={{
          padding: "14px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          minHeight: 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <img src={ZephyrLogo} alt="Zephyr Claims"
            style={{ height: 42, objectFit: "contain", maxWidth: "100%", filter: "brightness(0) invert(1)" }} />
        </div>

        <nav style={{ flex: 1, padding: "8px 8px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          {ZEPHYR_NAV.map(({ label, path, Icon }) => (
            <NavLink
              key={path}
              to={path}
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                borderRadius: 8,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: "1.26rem",
                color: isActive ? "#fff" : "rgba(255,255,255,0.68)",
                background: isActive ? `linear-gradient(90deg, ${Z_BLUE}55, ${Z_BLUE}22)` : "transparent",
                borderLeft: isActive ? `3px solid ${Z_BLUE}` : "3px solid transparent",
                transition: "all 0.15s",
              })}
            >
              <Icon style={{ fontSize: 22 }} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* ── User + Logout (Zephyr-only users only) ───────────── */}
        {zephyrOnly && (
          <div style={{ padding: "8px", borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", flexDirection: "column", gap: 6 }}>
            {/* User info */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", borderRadius: 8,
              background: "rgba(255,255,255,0.08)",
            }}>
              {userInfo?.picture && !imageError ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name || "User"}
                  style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div style={{
                  width: 30, height: 30, borderRadius: "50%",
                  backgroundColor: "#FFAF3C", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#002169", fontWeight: 700, fontSize: 13,
                }}>
                  {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {userInfo?.name || "User"}
                </p>
                <p style={{ margin: 0, fontSize: 10, color: "rgba(147,197,253,0.9)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {userInfo?.email || ""}
                </p>
              </div>
            </div>
            {/* Logout */}
            <button
              onClick={handleLogout}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "none", background: "transparent", cursor: "pointer",
                color: "#fca5a5", fontSize: "1.26rem", fontWeight: 600,
                transition: "all 0.15s", textAlign: "left",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(185,28,28,0.35)"; e.currentTarget.style.color = "#fecaca"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#fca5a5"; }}
            >
              <LogoutOutlinedIcon style={{ fontSize: 20 }} />
              Logout
            </button>
          </div>
        )}
      </div>

      {/* ── Main content ─────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <Outlet />
      </div>
    </div>
  );
}
